/**
 * @file Generic dropdown menu with Popper.js support.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Manager, Reference, Popper } from 'react-popper';
import classNames from 'classnames';

class DropdownMenu extends Component {
  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
    closeOnClickWithin: PropTypes.bool,
    placement: PropTypes.oneOf([
      'top',
      'top-start',
      'top-end',
      'right',
      'right-start',
      'right-end',
      'bottom',
      'bottom-start',
      'bottom-end',
      'left',
      'left-start',
      'left-end',
      'auto',
      'auto-start',
      'auto-end',
    ]),
    toggle: PropTypes.node,
    toggleLabel: PropTypes.node,
    toggleStyle: PropTypes.oneOf([
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'link',
    ]),
    toggleClassName: PropTypes.string,
    dropdownClassName: PropTypes.string,
    className: PropTypes.string,
    positionFixed: PropTypes.bool,
    startOpen: PropTypes.bool,
    closeOnMouseLeave: PropTypes.bool,
    onToggleSuccess: PropTypes.func,
    openOnHover: PropTypes.bool,
  };

  static defaultProps = {
    closeOnClickWithin: false,
    placement: 'bottom-start',
    toggleStyle: 'secondary',
    openOnHover: false,
  };

  state = {
    open: this.props.startOpen,
  };

  closeTimer = null;

  componentDidMount() {
    const { open } = this.state;

    if (open) {
      document.addEventListener('click', this.onClickOutside, false);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onClickOutside, false);
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
    }
  }

  onDropdownToggle = event => {
    const { onToggleSuccess } = this.props;
    // Allow downloading or opening tabs from links within the dropdown.
    const srcElement = event?.srcElement ?? event?.target;

    if (
      srcElement?.className === 'download-link' ||
      srcElement?.target === '_blank' ||
      (srcElement?.tagName === 'A' &&
        srcElement?.getAttribute('role') !== 'button')
    ) {
      return;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { open } = this.state;

    // Close the dropdown if we click outside of it
    if (!open) {
      document.addEventListener('click', this.onClickOutside, false);
    } else {
      document.removeEventListener('click', this.onClickOutside, false);
    }

    this.setState(state => ({
      open: !state.open,
    }));

    if (onToggleSuccess) {
      onToggleSuccess();
    }
  };

  onClickOutside = event => {
    if (this.node && this.node.contains(event.target)) {
      return;
    }

    this.onDropdownToggle(event);
  };

  handleMouseEnter = () => {
    const { open } = this.state;
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (!open) {
      document.addEventListener('click', this.onClickOutside, false);
      this.setState({ open: true });
    }
  };

  handleMouseLeave = () => {
    const { open } = this.state;
    if (open) {
      this.closeTimer = setTimeout(() => {
        document.removeEventListener('click', this.onClickOutside, false);
        this.setState({ open: false });
      }, 100);
    }
  };

  render() {
    const { open } = this.state;
    const {
      closeOnClickWithin,
      placement,
      toggle,
      toggleLabel,
      toggleStyle,
      toggleClassName,
      dropdownClassName,
      className,
      positionFixed,
      closeOnMouseLeave,
      children,
      openOnHover,
    } = this.props;

    const toggleClasses = classNames({
      btn: !toggle,
      'btn-reset': !!toggle,
      'dropdown-toggle': !toggle,
      [`btn-${toggleStyle}`]: !!toggleStyle && !toggle,
      [toggleClassName]: !!toggleClassName,
    });

    const dropdownClasses = classNames({
      'dropdown-menu': true,
      [dropdownClassName]: !!dropdownClassName,
    });

    return (
      <span
        className={className}
        onMouseEnter={openOnHover ? this.handleMouseEnter : undefined}
        onMouseLeave={openOnHover ? this.handleMouseLeave : undefined}
      >
        <Manager>
          <Reference>
            {({ ref }) => (
              <a
                role="button"
                href="#dropdown"
                ref={ref}
                className={toggleClasses}
                onClick={this.onDropdownToggle}
                aria-label="Dropdown Toggle"
                title="Dropdown Toggle"
              >
                {toggle || toggleLabel}
              </a>
            )}
          </Reference>

          {open && (
            <Popper
              placement={placement}
              positionFixed={positionFixed}
              innerRef={ref => {
                this.node = ref;
              }}
              eventsEnabled={open}
            >
              {({ ref, style, placement: popperPlacement }) => (
                <Fragment>
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                  <div
                    ref={ref}
                    style={style}
                    data-placement={popperPlacement}
                    className={dropdownClasses}
                    onClick={e => {
                      e.stopPropagation();
                      // Only close if closeOnClickWithin is true and the clicked element is not a regular link
                      const srcElement = e?.srcElement ?? e?.target;
                      if (
                        closeOnClickWithin &&
                        !(
                          srcElement?.tagName === 'A' &&
                          srcElement?.getAttribute('role') !== 'button'
                        )
                      ) {
                        this.onDropdownToggle(e);
                      }
                    }}
                    onMouseLeave={
                      closeOnMouseLeave
                        ? () => {
                            this.onDropdownToggle();
                          }
                        : undefined
                    }
                  >
                    {typeof children === 'function'
                      ? children({ onDropdownToggle: this.onDropdownToggle })
                      : children}
                  </div>
                </Fragment>
              )}
            </Popper>
          )}
        </Manager>
      </span>
    );
  }
}

export default DropdownMenu;
